import { zodResolver } from "@hookform/resolvers/zod";
import { teamLabelForJobTitle } from "@shared/constants/org-teams";
import type { Employee } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useRoute } from "wouter";
import { z } from "zod";
import { InlineEditField } from "@/components/inline-edit-field";
import { PageHeader } from "@/components/page-header";
import type { AnswerData, QuestionData } from "@/components/surveys/survey-response-viewer";
import { SurveyResponseViewer } from "@/components/surveys/survey-response-viewer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/use-auth";
import { useEmployeeAccess } from "@/hooks/use-employee-access";
import { useSurveyAccess } from "@/hooks/use-survey-access";
import { useToast } from "@/hooks/use-toast";
import {
  COMP_DISCIPLINES,
  DEPARTMENTS,
  EMPLOYEE_CURRENCIES,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  formatCurrency,
  formatDate,
  getCompDisciplineLabel,
  getCurrencyLabel,
  getDepartmentLabel,
  getEmploymentTypeLabel,
  getJobLevelLabel,
  getStatusLabel,
  getWorkTypeLabel,
  JOB_LEVELS,
  parseLocalDate,
  WORK_TYPES,
} from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";

const compensationFormSchema = z.object({
  type: z.string().min(1, "Tipo é obrigatório"),
  amount: z.string().min(1, "Valor é obrigatório"),
});

type CompensationFormValues = z.infer<typeof compensationFormSchema>;

const COMPENSATION_TYPES = [
  { value: "initial", label: "salário inicial" },
  { value: "salary_raise", label: "aumento salarial" },
  { value: "bonus", label: "bônus" },
  { value: "commission", label: "comissão" },
];

const COMPENSATION_TYPE_VERB: Record<string, string> = {
  initial: "definiu salário inicial",
  salary_raise: "aprovou aumento",
  bonus: "lançou bônus",
  commission: "lançou comissão",
};

function nowHHmm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Word-Carries-State para status do colaborador no header narrativo
function statusVerb(status: string | null | undefined) {
  switch (status) {
    case "active":
      return { verb: "Ativa", color: "text-[hsl(var(--forest-pulse))]" };
    case "onboarding":
      return { verb: "Em onboarding", color: "text-[hsl(var(--ink-primary))]" };
    case "inactive":
    case "terminated":
    case "archived":
      return { verb: "Inativa", color: "text-[hsl(var(--ink-muted))]" };
    case "pending_action":
    case "pending_approval":
    case "waiting_approval":
      return { verb: "Pendente", color: "text-[hsl(var(--comp-coral))]" };
    default:
      return {
        verb: EMPLOYEE_STATUSES.find((s) => s.value === status)?.label || status || "—",
        color: "text-[hsl(var(--ink-muted))]",
      };
  }
}

export interface EmployeeDetailsProps {
  embedded?: boolean;
  employeeId?: string;
}

export default function EmployeeDetails(props?: EmployeeDetailsProps) {
  const { embedded = false, employeeId: propEmployeeId } = props || {};
  const [, params] = useRoute("/employees/:id");
  const employeeId = propEmployeeId || params?.id;
  const [isCompensationOpen, setIsCompensationOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { isSuperAdmin, hasPermission, employee: currentEmployee } = useAuth();
  const { canViewSurveyResponses } = useSurveyAccess();
  const { canMaintainEmployeeBase, isEmployeeOperator } = useEmployeeAccess();

  // --- Data fetching ---
  const { data: employee, isLoading: loadingEmployee } = useQuery(
    orpc.employees.getById.queryOptions({ input: { id: employeeId! }, enabled: !!employeeId }),
  );

  const isDirectManagerOfEmployee =
    !!currentEmployee && employee?.directManagerId === currentEmployee.id;
  // An Operador de Colaboradores receives this record without compensation or
  // personal data (the server projects it) unless they are this person's
  // direct manager — so compensation isn't fetched and those fields are hidden.
  const isOperatorView = isEmployeeOperator && !isDirectManagerOfEmployee;

  const { data: compensation, isLoading: loadingCompensation } = useQuery(
    orpc.employees.getCompensation.queryOptions({
      input: { id: employeeId! },
      enabled: !!employeeId && !!employee && !isOperatorView,
    }),
  );

  // Performance and Pulse are separate products with their own permission keys.
  // Holding `employees` grants the colaborador's record, not these; permissions
  // compose, so `employees` + `performance` sees the Performance tab. Mirrors
  // each server rule (own key OR this employee's direct manager) so we never
  // fire a request that 403s and trips the global "Sem permissão" toast.
  const canSeePerformance = hasPermission("performance") || isDirectManagerOfEmployee;
  const canSeePulse = canViewSurveyResponses || isDirectManagerOfEmployee;

  const { data: performanceCycles, isLoading: loadingPerformance } = useQuery({
    ...orpc.performanceCycles.listByEmployee.queryOptions({ input: { id: employeeId! } }),
    enabled: !!employeeId && canSeePerformance,
  });

  const { data: pulseResponses, isLoading: loadingPulse } = useQuery({
    ...orpc.performanceCycles.listPulseResponses.queryOptions({ input: { id: employeeId! } }),
    enabled: !!employeeId && canSeePulse,
  });

  const { data: people } = useQuery(orpc.dashboard.people.queryOptions());

  // --- Mutations ---
  const updateEmployee = useMutation({
    ...orpc.employees.updateField.mutationOptions(),
    onSuccess: ({ employee: updatedEmployee }) => {
      queryClient.setQueryData(
        orpc.employees.getById.queryOptions({ input: { id: employeeId! } }).queryKey,
        updatedEmployee,
      );
      queryClient.setQueryData(
        orpc.employees.list.queryOptions().queryKey,
        (oldData: Employee[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((e) => (e.id === employeeId ? updatedEmployee : e));
        },
      );
      toast({ description: `Salvo às ${nowHHmm()}.` });
    },
    onError: (error: any) => {
      if (error.status === 409) {
        toast({
          description:
            "Registro modificado por outro usuário. Recarregue a página e tente de novo.",
          variant: "destructive",
        });
        queryClient.invalidateQueries({
          queryKey: orpc.employees.getById.queryOptions({ input: { id: employeeId! } }).queryKey,
        });
      } else {
        toast({
          description: error.message || "Falha ao atualizar colaborador. Tente novamente.",
          variant: "destructive",
        });
      }
    },
  });

  const compensationForm = useForm<CompensationFormValues>({
    resolver: zodResolver(compensationFormSchema),
    defaultValues: {
      type: "",
      amount: "",
    },
  });

  const createCompensation = useMutation({
    ...orpc.employees.addCompensation.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.employees.getCompensation.queryOptions({ input: { id: employeeId! } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.employees.getById.queryOptions({ input: { id: employeeId! } }).queryKey,
      });
      setIsCompensationOpen(false);
      compensationForm.reset();
      toast({ description: `Entrada de compensação lançada às ${nowHHmm()}.` });
    },
    onError: (error: Error) => {
      toast({
        description: error.message || "Falha ao lançar compensação. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const deleteEmployee = useMutation({
    ...orpc.employees.delete.mutationOptions(),
    onSuccess: () => {
      const name = employee?.fullName ?? "Colaborador";
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      toast({ description: `${name} removido.` });
      navigate("/employees");
    },
    onError: (error: Error) => {
      toast({
        description: error.message || "Falha ao remover colaborador. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  // --- Inline edit handler (uses field-level API) ---
  const handleInlineSave = useCallback(
    async (fieldName: string, value: string) => {
      await updateEmployee.mutateAsync({
        id: employeeId!,
        field: fieldName,
        value: value || null,
      });
    },
    [updateEmployee, employeeId],
  );

  const onSubmitCompensation = (data: CompensationFormValues) => {
    createCompensation.mutate({ id: employeeId!, ...data });
  };

  // --- Helpers ---
  const getCompensationTypeLabel = (type: string) => {
    return COMPENSATION_TYPES.find((t) => t.value === type)?.label || type;
  };

  const getCycleTypeLabel = (type: string) => {
    switch (type) {
      case "first_3m":
        return "Avaliação 3 meses";
      case "annual":
        return "Avaliação anual";
      default:
        return type;
    }
  };

  const getManagerName = (managerId: string | null | undefined) => {
    if (!managerId) return "—";
    const manager = people?.find((p) => p.id === managerId);
    return manager?.fullName || "—";
  };

  // --- Loading / not found ---
  if (loadingEmployee) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-2/3 rounded bg-[hsl(var(--sand-step))] animate-pulse" aria-hidden />
        <div className="h-4 w-1/3 rounded bg-[hsl(var(--sand-step))] animate-pulse" aria-hidden />
        <div className="space-y-2 pt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
              key={i}
              className="h-3 rounded bg-[hsl(var(--sand-step))] animate-pulse"
              aria-hidden
            />
          ))}
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-6">
        <p className="text-[hsl(var(--ink-muted))]">Colaborador não encontrado.</p>
        {!embedded && (
          <Link href="/employees">
            <Button variant="outline" className="mt-4">
              Voltar para colaboradores
            </Button>
          </Link>
        )}
      </div>
    );
  }

  // Current salary in centavos -> format
  const currentSalaryDisplay = employee.currentSalary
    ? formatCurrency(employee.currentSalary, employee.currency)
    : "—";

  // Sort compensation by date (newest first)
  const sortedCompensation = compensation
    ? [...compensation].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      })
    : [];

  const { verb: statusWord, color: statusColor } = statusVerb(employee.status);
  const sinceLabel = employee.startDate ? formatDate(employee.startDate) : null;
  const departmentLabel = employee.department ? getDepartmentLabel(employee.department) : "";
  const levelLabel = employee.jobLevel ? getJobLevelLabel(employee.jobLevel) : "";
  const subline = [employee.jobTitle, departmentLabel, levelLabel].filter(Boolean).join(" · ");

  return (
    <div className={`h-full overflow-y-auto ${embedded ? "overflow-x-hidden" : ""}`}>
      <div className={`space-y-8 ${embedded ? "min-w-0" : ""}`}>
        {!embedded && (
          <>
            <PageHeader title="Colaboradores" backTo="/employees" />

            {/* Hero — Display Section + atmospheric wash, status em prosa narrativa, sem avatar */}
            <div className="atmospheric-wash pt-4 pb-6 px-1">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <h1 className="display-section text-[hsl(var(--ink-primary))]">
                    {employee.fullName}
                  </h1>
                  {subline && <p className="text-sm text-[hsl(var(--ink-muted))]">{subline}</p>}
                  <p className="text-sm text-[hsl(var(--ink-muted))]">
                    <span className={`body-strong ${statusColor}`}>{statusWord}</span>
                    {sinceLabel ? (
                      <>
                        {" "}
                        desde <span className="num-mono">{sinceLabel}</span>
                      </>
                    ) : null}
                    {employee.directManagerId ? (
                      <>
                        {" "}
                        · reporta para{" "}
                        <span className="text-[hsl(var(--ink-primary))]">
                          {getManagerName(employee.directManagerId)}
                        </span>
                      </>
                    ) : null}
                    .
                  </p>
                </div>
                {isSuperAdmin && (
                  <Button
                    variant="destructive"
                    size="sm"
                    data-testid="button-delete-employee"
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Remover colaborador
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Tabs — sem ícones, body-strong na ativa, hairline-bottom */}
        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList
            className={`bg-transparent p-0 h-auto gap-6 rounded-none border-b border-[hsl(var(--card-line))] ${embedded ? "flex flex-wrap" : ""}`}
          >
            <TabsTrigger
              value="personal"
              data-testid="tab-personal"
              className="bg-transparent shadow-none rounded-none px-0 pb-2 data-[state=active]:bg-transparent data-[state=active]:body-strong data-[state=active]:text-[hsl(var(--ink-primary))] data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--comp-coral))] data-[state=active]:shadow-none text-[hsl(var(--ink-muted))]"
            >
              Perfil
            </TabsTrigger>
            {!isOperatorView && (
              <TabsTrigger
                value="compensation"
                data-testid="tab-compensation"
                className="bg-transparent shadow-none rounded-none px-0 pb-2 data-[state=active]:bg-transparent data-[state=active]:body-strong data-[state=active]:text-[hsl(var(--ink-primary))] data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--comp-coral))] data-[state=active]:shadow-none text-[hsl(var(--ink-muted))]"
              >
                Compensação
              </TabsTrigger>
            )}
            {canSeePerformance && (
              <TabsTrigger
                value="performance"
                data-testid="tab-performance"
                className="bg-transparent shadow-none rounded-none px-0 pb-2 data-[state=active]:bg-transparent data-[state=active]:body-strong data-[state=active]:text-[hsl(var(--ink-primary))] data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--comp-coral))] data-[state=active]:shadow-none text-[hsl(var(--ink-muted))]"
              >
                Performance
              </TabsTrigger>
            )}
            {canSeePulse && (
              <TabsTrigger
                value="pulse"
                data-testid="tab-pulse"
                className="bg-transparent shadow-none rounded-none px-0 pb-2 data-[state=active]:bg-transparent data-[state=active]:body-strong data-[state=active]:text-[hsl(var(--ink-primary))] data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--comp-coral))] data-[state=active]:shadow-none text-[hsl(var(--ink-muted))]"
              >
                Pulse
              </TabsTrigger>
            )}
          </TabsList>

          {/* ====== Tab Perfil ====== */}
          <TabsContent value="personal" className="space-y-8">
            <section className="space-y-3">
              <h3 className="caption-mono">Dados profissionais</h3>
              <div
                className={`grid ${embedded ? "grid-cols-1 divide-y divide-[hsl(var(--card-line))]" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"}`}
              >
                <InlineEditField
                  label="Cargo"
                  value={employee.jobTitle}
                  fieldName="jobTitle"
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Time"
                  value={teamLabelForJobTitle(employee.jobTitle)}
                  fieldName="team"
                  disabled
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Disciplina comp"
                  value={employee.compDiscipline}
                  fieldName="compDiscipline"
                  type="select"
                  options={COMP_DISCIPLINES.map((d) => ({ value: d.value, label: d.label }))}
                  displayValue={
                    employee.compDiscipline
                      ? getCompDisciplineLabel(employee.compDiscipline)
                      : undefined
                  }
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="E-mail corporativo"
                  value={employee.corporateEmail}
                  fieldName="corporateEmail"
                  disabled={isOperatorView}
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Nível"
                  value={employee.jobLevel}
                  fieldName="jobLevel"
                  type="select"
                  options={JOB_LEVELS.map((l) => ({ value: l.value, label: l.label }))}
                  displayValue={employee.jobLevel ? getJobLevelLabel(employee.jobLevel) : undefined}
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Departamento"
                  value={employee.department}
                  fieldName="department"
                  type="select"
                  options={DEPARTMENTS.map((d) => ({ value: d.value, label: d.label }))}
                  displayValue={
                    employee.department ? getDepartmentLabel(employee.department) : undefined
                  }
                  onSave={handleInlineSave}
                />
                {!isOperatorView && (
                  <>
                    <InlineEditField
                      label="Tipo de contrato"
                      value={employee.employmentType}
                      fieldName="employmentType"
                      type="select"
                      options={EMPLOYMENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                      displayValue={getEmploymentTypeLabel(employee.employmentType)}
                      onSave={handleInlineSave}
                    />
                    <InlineEditField
                      label="Modelo de trabalho"
                      value={employee.workType}
                      fieldName="workType"
                      type="select"
                      options={WORK_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                      displayValue={getWorkTypeLabel(employee.workType)}
                      onSave={handleInlineSave}
                    />
                  </>
                )}
                <InlineEditField
                  label="Status"
                  value={employee.status}
                  fieldName="status"
                  type="select"
                  options={EMPLOYEE_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
                  displayValue={getStatusLabel(employee.status)}
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Data de início"
                  value={employee.startDate}
                  fieldName="startDate"
                  type="date"
                  disabled={isOperatorView}
                  displayValue={employee.startDate ? formatDate(employee.startDate) : undefined}
                  onSave={handleInlineSave}
                />
                <InlineEditField
                  label="Data de término"
                  value={employee.endDate}
                  fieldName="endDate"
                  type="date"
                  displayValue={employee.endDate ? formatDate(employee.endDate) : undefined}
                  onSave={handleInlineSave}
                />
                {!isOperatorView && (
                  <>
                    <InlineEditField
                      label="Salário atual"
                      value={currentSalaryDisplay}
                      fieldName="currentSalary"
                      disabled
                      onSave={handleInlineSave}
                    />
                    <InlineEditField
                      label="Moeda"
                      value={employee.currency}
                      fieldName="currency"
                      type="select"
                      options={EMPLOYEE_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))}
                      displayValue={getCurrencyLabel(employee.currency)}
                      onSave={handleInlineSave}
                    />
                  </>
                )}
                {isSuperAdmin && (
                  <InlineEditField
                    label="Apto a ser Buddy"
                    value={employee.buddyEligible ? "true" : "false"}
                    fieldName="buddyEligible"
                    type="select"
                    options={[
                      { value: "true", label: "Sim" },
                      { value: "false", label: "Não" },
                    ]}
                    displayValue={employee.buddyEligible ? "Sim" : "Não"}
                    onSave={async (fieldName, value) => {
                      await updateEmployee.mutateAsync({
                        id: employeeId!,
                        field: fieldName,
                        value: value === "true",
                      });
                    }}
                  />
                )}
                <InlineEditField
                  label="Gestor direto"
                  value={employee.directManagerId}
                  fieldName="directManagerId"
                  type="select"
                  disabled={!canMaintainEmployeeBase}
                  options={[
                    { value: "__none__", label: "Nenhum" },
                    ...(people
                      ?.filter((p) => p.id !== employeeId)
                      .map((p) => ({ value: p.id, label: p.fullName })) || []),
                  ]}
                  displayValue={getManagerName(employee.directManagerId)}
                  onSave={async (fieldName, value) => {
                    await handleInlineSave(fieldName, value === "__none__" ? "" : value);
                  }}
                />
              </div>
            </section>

            {!isOperatorView && (
              <section className="space-y-3">
                <h3 className="caption-mono">Dados pessoais</h3>
                <div
                  className={`grid ${embedded ? "grid-cols-1 divide-y divide-[hsl(var(--card-line))]" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"}`}
                >
                  <InlineEditField
                    label="Nome completo"
                    value={employee.fullName}
                    fieldName="fullName"
                    onSave={handleInlineSave}
                  />
                  <InlineEditField
                    label="E-mail pessoal"
                    value={employee.personalEmail}
                    fieldName="personalEmail"
                    onSave={handleInlineSave}
                  />
                  <InlineEditField
                    label="Telefone"
                    value={employee.phone}
                    fieldName="phone"
                    onSave={handleInlineSave}
                  />
                  <InlineEditField
                    label="CPF"
                    value={employee.cpf}
                    fieldName="cpf"
                    onSave={handleInlineSave}
                  />
                  <InlineEditField
                    label="Data de nascimento"
                    value={employee.birthdate}
                    fieldName="birthdate"
                    type="date"
                    displayValue={employee.birthdate ? formatDate(employee.birthdate) : undefined}
                    onSave={handleInlineSave}
                  />
                  <InlineEditField
                    label="Endereço"
                    value={employee.address}
                    fieldName="address"
                    onSave={handleInlineSave}
                  />
                </div>
              </section>
            )}
          </TabsContent>

          {/* ====== Tab Compensação ====== */}
          {!isOperatorView && (
            <TabsContent value="compensation" className="space-y-6">
              <div className="flex items-end justify-between gap-4">
                <div className="space-y-1">
                  <span className="caption-mono">Salário atual</span>
                  <p className="display-section num-mono text-[hsl(var(--ink-primary))]">
                    {currentSalaryDisplay}
                  </p>
                </div>
                <Dialog open={isCompensationOpen} onOpenChange={setIsCompensationOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" data-testid="button-add-compensation">
                      Lançar entrada
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-modal" overlayClassName="glass-scrim">
                    <DialogHeader>
                      <div className="flex items-center justify-between gap-3">
                        <DialogTitle>Lançar entrada de compensação</DialogTitle>
                        <span className="caption-mono text-[hsl(var(--ink-muted))]">
                          Esc para fechar
                        </span>
                      </div>
                    </DialogHeader>
                    <Form {...compensationForm}>
                      <form
                        onSubmit={compensationForm.handleSubmit(onSubmitCompensation)}
                        className="space-y-4"
                      >
                        <FormField
                          control={compensationForm.control}
                          name="type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="caption-mono">Tipo</FormLabel>
                              <SearchableSelect
                                onValueChange={field.onChange}
                                value={field.value}
                                options={COMPENSATION_TYPES.map((type) => ({
                                  value: type.value,
                                  label: type.label,
                                }))}
                                placeholder="Selecione o tipo"
                                data-testid="select-compensation-type"
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={compensationForm.control}
                          name="amount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="caption-mono">
                                Valor (
                                {EMPLOYEE_CURRENCIES.find((c) => c.value === employee.currency)
                                  ?.symbol ?? "R$"}
                                )
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="number"
                                  step="0.01"
                                  className="num-mono"
                                  data-testid="input-compensation-amount"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <DialogFooter>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsCompensationOpen(false)}
                          >
                            Fechar
                          </Button>
                          <Button
                            type="submit"
                            disabled={createCompensation.isPending}
                            data-testid="button-submit-compensation"
                          >
                            {createCompensation.isPending ? "Lançando…" : "Lançar entrada"}
                          </Button>
                        </DialogFooter>
                      </form>
                    </Form>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Histórico narrativo — DESIGN v7 §7 (prosa, não pill) */}
              {loadingCompensation ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                      key={i}
                      className="h-4 rounded bg-[hsl(var(--sand-step))] animate-pulse"
                      aria-hidden
                    />
                  ))}
                </div>
              ) : sortedCompensation.length > 0 ? (
                <ul className="space-y-3">
                  {sortedCompensation.map((entry, idx) => {
                    const previous = sortedCompensation[idx + 1];
                    const newVal = formatCurrency(entry.amount, employee.currency);
                    const verb =
                      COMPENSATION_TYPE_VERB[entry.type] ||
                      `lançou ${getCompensationTypeLabel(entry.type)}`;
                    const date = entry.createdAt ? formatDate(entry.createdAt) : "";
                    return (
                      <li
                        key={entry.id}
                        className="text-sm text-[hsl(var(--ink-primary))] py-2 border-b border-[hsl(var(--card-line))] last:border-b-0"
                        data-testid={`compensation-entry-${entry.id}`}
                      >
                        <span className="body-strong">Você</span>{" "}
                        <span className="body-strong text-[hsl(var(--forest-pulse))]">{verb}</span>
                        {entry.type === "salary_raise" && previous ? (
                          <>
                            {" "}
                            de{" "}
                            <span className="num-mono">
                              {formatCurrency(previous.amount, employee.currency)}
                            </span>{" "}
                            para <span className="num-mono body-strong">{newVal}</span>
                          </>
                        ) : (
                          <>
                            {" "}
                            no valor de <span className="num-mono body-strong">{newVal}</span>
                          </>
                        )}
                        {date ? (
                          <>
                            {" "}
                            em <span className="num-mono">{date}</span>
                          </>
                        ) : null}
                        .
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-[hsl(var(--ink-muted))]">
                  Nenhuma entrada de compensação ainda. Lance a primeira em{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setIsCompensationOpen(true)}
                  >
                    Lançar entrada
                  </button>
                  .
                </p>
              )}
            </TabsContent>
          )}

          {/* ====== Tab Performance ====== */}
          <TabsContent value="performance" className="space-y-4">
            {loadingPerformance ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                    key={i}
                    className="h-4 rounded bg-[hsl(var(--sand-step))] animate-pulse"
                    aria-hidden
                  />
                ))}
              </div>
            ) : performanceCycles && performanceCycles.length > 0 ? (
              <Accordion type="single" collapsible className="space-y-2">
                {[...performanceCycles]
                  .sort((a, b) => {
                    const da = a.dueDate ? parseLocalDate(a.dueDate).getTime() : 0;
                    const db2 = b.dueDate ? parseLocalDate(b.dueDate).getTime() : 0;
                    return db2 - da;
                  })
                  .map((cycle) => {
                    const hasContent =
                      (cycle.managerAssessments && cycle.managerAssessments.length > 0) ||
                      (cycle.outcomes && cycle.outcomes.length > 0);
                    const { verb: cv, color: cc } = statusVerb(cycle.status);
                    return (
                      <AccordionItem
                        key={cycle.id}
                        value={cycle.id}
                        className="border-b border-[hsl(var(--card-line))] px-1"
                        data-testid={`performance-cycle-${cycle.id}`}
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between flex-1 mr-4">
                            <div className="flex items-center gap-3">
                              <span className="body-strong">
                                {getCycleTypeLabel(cycle.cycleType)}
                              </span>
                              <span className={`body-strong ${cc}`}>{cv}</span>
                            </div>
                            <span className="caption-mono">
                              Prazo: <span className="num-mono">{formatDate(cycle.dueDate)}</span>
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {hasContent ? (
                            <div className="space-y-4 pb-2">
                              {cycle.managerAssessments &&
                                cycle.managerAssessments.length > 0 &&
                                cycle.managerAssessments.map((assessment) => {
                                  const { verb: rv, color: rc } = statusVerb(
                                    assessment.ratingFinal,
                                  );
                                  return (
                                    <div key={assessment.id} className="space-y-2">
                                      <p className="text-sm text-[hsl(var(--ink-primary))]">
                                        Resultado: <span className={`body-strong ${rc}`}>{rv}</span>
                                      </p>
                                      {assessment.observations && (
                                        <p className="text-sm text-[hsl(var(--ink-muted))]">
                                          {assessment.observations}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}

                              {cycle.outcomes &&
                                cycle.outcomes.length > 0 &&
                                cycle.outcomes.map((outcome) => {
                                  const { verb: ov, color: oc } = statusVerb(outcome.ratingFinal);
                                  return (
                                    <div
                                      key={outcome.id}
                                      className="space-y-2 pt-3 border-t border-[hsl(var(--card-line))]"
                                    >
                                      <p className="text-sm">
                                        Avaliação final:{" "}
                                        <span className={`body-strong ${oc}`}>{ov}</span>. Aumento
                                        de{" "}
                                        <span className="body-strong num-mono">
                                          {outcome.salaryIncreasePct}%
                                        </span>
                                        {outcome.newSalaryBase ? (
                                          <>
                                            , novo salário{" "}
                                            <span className="body-strong num-mono">
                                              {formatCurrency(
                                                outcome.newSalaryBase,
                                                employee.currency,
                                              )}
                                            </span>
                                          </>
                                        ) : null}
                                        .
                                      </p>
                                      {outcome.adminNotes && (
                                        <p className="text-sm text-[hsl(var(--ink-muted))]">
                                          {outcome.adminNotes}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <p className="text-sm text-[hsl(var(--ink-muted))] pb-2">
                              Ciclo ainda não avaliado.
                            </p>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
              </Accordion>
            ) : (
              <p className="text-sm text-[hsl(var(--ink-muted))]">
                Nenhum ciclo de performance encontrado.
              </p>
            )}
          </TabsContent>

          {/* ====== Tab Pulse ====== */}
          <TabsContent value="pulse" className="space-y-4">
            {loadingPulse ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                    key={i}
                    className="h-4 rounded bg-[hsl(var(--sand-step))] animate-pulse"
                    aria-hidden
                  />
                ))}
              </div>
            ) : pulseResponses && pulseResponses.length > 0 ? (
              <Accordion type="single" collapsible className="space-y-2">
                {[...pulseResponses]
                  .sort((a, b) => {
                    const da = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
                    const db2 = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
                    return db2 - da;
                  })
                  .map((response) => {
                    const questions: QuestionData[] = (response.answers || [])
                      .filter((a) => a.question)
                      .map((a) => ({
                        id: a.question!.id,
                        questionText: a.question!.questionText,
                        description: a.question!.description ?? null,
                        questionType: a.question!.questionType,
                        required: false,
                        scaleMin: a.question!.scaleMin ?? null,
                        scaleMax: a.question!.scaleMax ?? null,
                        scaleMinLabel: a.question!.scaleMinLabel ?? null,
                        scaleMaxLabel: a.question!.scaleMaxLabel ?? null,
                        options: a.question!.options ?? null,
                      }));

                    const answers: AnswerData[] = (response.answers || [])
                      .filter((a) => a.question)
                      .map((a) => ({
                        questionId: a.question!.id,
                        answerText: a.answerText,
                        answerValue: a.answerValue,
                      }));

                    return (
                      <AccordionItem
                        key={response.id}
                        value={response.id}
                        className="border-b border-[hsl(var(--card-line))] px-1"
                        data-testid={`pulse-response-${response.id}`}
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between flex-1 mr-4">
                            <span className="body-strong">
                              {response.survey?.title || "Pesquisa sem título"}
                            </span>
                            <span className="caption-mono">
                              {response.submittedAt ? (
                                <span className="num-mono">{formatDate(response.submittedAt)}</span>
                              ) : (
                                "Em andamento"
                              )}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          {questions.length > 0 ? (
                            <SurveyResponseViewer questions={questions} answers={answers} />
                          ) : (
                            <p className="text-sm text-[hsl(var(--ink-muted))]">
                              Sem respostas detalhadas.
                            </p>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
              </Accordion>
            ) : (
              <p className="text-sm text-[hsl(var(--ink-muted))]">
                Nenhuma resposta de pulse survey ainda.
              </p>
            )}
          </TabsContent>
        </Tabs>

        {/* Delete Confirmation — destructive dialog (DESIGN v7 §11) */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent className="glass-modal" overlayClassName="glass-scrim">
            <DialogHeader>
              <div className="flex items-center justify-between gap-3">
                <DialogTitle>Remover colaborador {employee.fullName}?</DialogTitle>
                <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
              </div>
              <DialogDescription>
                Esta ação é irreversível. Histórico de compensação, ciclos de performance, respostas
                de pulse survey, planos de onboarding e feedbacks de entrevista deste colaborador
                serão arquivados.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                data-testid="button-cancel-delete"
              >
                Fechar
              </Button>
              <Button
                variant="destructive"
                data-testid="button-confirm-delete-employee"
                onClick={() => deleteEmployee.mutate({ id: employeeId! })}
                disabled={deleteEmployee.isPending}
              >
                {deleteEmployee.isPending ? "Removendo…" : "Remover colaborador"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
